import { typographyTokens } from '../../../../theme/typographyTokens';
import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TaskTemplate } from '../../../../../shared/types/Task';

interface TaskSelectionTableProps {
  taskTemplatesData: TaskTemplate[] | undefined;
  filteredTasks: TaskTemplate[];
  selectedTasks: number[];
  taskSearch: string;
  tasksLoading: boolean;
  onTaskSearchChange: (value: string) => void;
  onTaskSelect: (taskId: number) => void;
  onSelectAllTasks: () => void;
}

const TaskSelectionTable: React.FC<TaskSelectionTableProps> = ({
  taskTemplatesData,
  filteredTasks,
  selectedTasks,
  taskSearch,
  tasksLoading,
  onTaskSearchChange,
  onTaskSelect,
  onSelectAllTasks,
}) => {
  return (
    <Box sx={{ flex: 1, minWidth: 400 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold, mb: 2 }}>
          Validation Task Templates ({taskTemplatesData?.length || 0})
        </Typography>

        <TextField
          size="small"
          placeholder="Search tasks by ID, name, or description..."
          fullWidth
          value={taskSearch}
          onChange={(e) => onTaskSearchChange(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {selectedTasks.length > 0 && (
          <Chip
            label={`${selectedTasks.length} task(s) selected`}
            color="secondary"
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      filteredTasks.length > 0 &&
                      selectedTasks.length === filteredTasks.length
                    }
                    indeterminate={
                      selectedTasks.length > 0 &&
                      selectedTasks.length < filteredTasks.length
                    }
                    onChange={onSelectAllTasks}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Short Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">
                      {tasksLoading
                        ? 'Loading...'
                        : 'No validation task templates found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow
                    key={task.id}
                    hover
                    selected={selectedTasks.includes(task.id)}
                    onClick={() => onTaskSelect(task.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedTasks.includes(task.id)} />
                    </TableCell>
                    <TableCell>{task.id}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={task.name || ''}
                      >
                        {task.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={task.shortName || ''}
                      >
                        {task.shortName || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TaskSelectionTable;
